import clsx from 'clsx';
import TooltipCustom from 'core/modules/atoms/Tooltip';
import React from 'react';
import { IconName } from 'ui/icons';
import Icon from '../Icon/Icon';

type TagColor = 'default' | 'primary' | 'success' | 'blue' | 'transparent' | 'brand-solid' | 'error';
type TagSize = 'sm' | 'md' | 'lg';
type TagShape = 'pill' | 'rounded';

type TagProps = {
  ariaLabel?: string;
  className?: string;
  color?: TagColor;
  customBackgroundColor?: string;
  customTextColor?: string;
  hasBorder?: boolean;
  isFullWidth?: boolean;
  label: string | React.ReactNode;
  leftIcon?: IconName;
  onClick?: () => void;
  rightIcon?: IconName;
  shape?: TagShape;
  shouldTruncate?: boolean;
  size?: TagSize;
  tooltip?: string;
  tooltipClassName?: string;
};

function IconSlot({ name }: Readonly<{ name?: IconName }>): JSX.Element | null {
  if (!name) return null;
  return (
    <span aria-hidden="true" className="tag__icon">
      <Icon color="inherit" name={name} />
    </span>
  );
}

function buildInlineStyle(
  backgroundColor: string | undefined,
  textColor: string | undefined,
): React.CSSProperties | undefined {
  if (!backgroundColor && !textColor) return undefined;
  const styles: Record<string, string> = {};
  if (backgroundColor) {
    styles.background = backgroundColor;
    styles['--tag-bg'] = backgroundColor;
  }
  if (textColor) {
    styles.color = textColor;
    styles['--tag-text-color'] = textColor;
  }
  return styles as React.CSSProperties;
}

function warnIfMissingAccessibleName(
  isInteractive: boolean,
  label: string | React.ReactNode,
  ariaLabel: string | undefined,
): void {
  if (process.env.NODE_ENV === 'production' || !isInteractive) return;
  const isLabelEmpty = label === '' || label === null || label === undefined;
  if (isLabelEmpty && !ariaLabel) {
    console.warn('[Tag] Interactive Tag (onClick set) has no accessible name. Provide a non-empty label or ariaLabel.');
  }
}

function Tag({
  ariaLabel,
  className,
  color = 'default',
  customBackgroundColor,
  customTextColor,
  hasBorder,
  isFullWidth = false,
  label,
  leftIcon,
  onClick,
  rightIcon,
  shape = 'pill',
  shouldTruncate = false,
  size = 'md',
  tooltip,
  tooltipClassName,
}: Readonly<TagProps>): JSX.Element {
  const hasOnClick = typeof onClick === 'function';
  const isFocusableForTooltip = Boolean(tooltip) && !hasOnClick;
  const shouldRenderAsButton = hasOnClick || isFocusableForTooltip;

  warnIfMissingAccessibleName(hasOnClick, label, ariaLabel);

  const rootClassName = clsx(
    'tag',
    `tag--color-${color}`,
    `tag--size-${size}`,
    `tag--shape-${shape}`,
    {
      'tag--clickable': hasOnClick,
      'tag--full-width': isFullWidth,
      'tag--truncate': shouldTruncate,
      'tag--has-border': hasBorder === true,
      'tag--no-border': hasBorder === false,
    },
    className,
  );

  const inlineStyle = buildInlineStyle(customBackgroundColor, customTextColor);

  const content = (
    <>
      <IconSlot name={leftIcon} />
      <span className="tag__label">{label}</span>
      <IconSlot name={rightIcon} />
    </>
  );

  const tagElement = shouldRenderAsButton ? (
    <button aria-label={ariaLabel} className={rootClassName} onClick={onClick} style={inlineStyle} type="button">
      {content}
    </button>
  ) : (
    <div aria-label={ariaLabel} className={rootClassName} style={inlineStyle}>
      {content}
    </div>
  );

  if (!tooltip) return tagElement;

  return (
    <TooltipCustom
      classes={{ tooltip: clsx('tag-tooltip', tooltipClassName), popper: 'tag-tooltip__popper' }}
      placement="top"
      title={tooltip}
    >
      {tagElement}
    </TooltipCustom>
  );
}

export default Tag;
